import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Mail, Phone, Calendar, KeyRound, Car, FileText, ExternalLink, DollarSign, Wallet, Percent, CalendarClock } from "lucide-react";
import { AdminVerifiedBadge } from "@/components/AdminVerifiedBadge";
import { api, fileUrl } from "@/lib/api";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");
const money = (n, c = "USD") => `${c === "USD" ? "$" : c + " "}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_CLS = {
  approved: "text-emerald-400 border-emerald-400/30",
  confirmed: "text-emerald-400 border-emerald-400/30",
  completed: "text-cyan-400 border-cyan-400/30",
  rejected: "text-[#FF5E7E] border-[#FF5E7E]/30",
  cancelled: "text-[#FF5E7E] border-[#FF5E7E]/30",
  pending: "text-amber-400 border-amber-400/30",
};
const badge = (s) => STATUS_CLS[s] || "text-slate-400 border-slate-400/30";

export default function AdminCarOperator() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [op, setOp] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/car-providers/${id}`).then(({ data }) => setOp(data)).catch(() => setOp(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (op === false) return <div className="min-h-screen bg-[#060810] flex items-center justify-center text-slate-400">Operator not found.</div>;
  if (!op) return <div className="min-h-screen bg-[#060810] flex items-center justify-center"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>;

  const profile = op.profile || {};
  const stats = op.stats || {};
  const DOCS = [
    ["Business License", profile.business_license_url],
    ["Fleet Insurance", profile.fleet_insurance_url],
    ["Rental Agreement", profile.rental_agreement_url],
  ];
  const STAT_CARDS = [
    { label: "Registered Vehicles", value: stats.vehicles_count ?? 0, icon: Car, color: "text-cyan-400" },
    { label: "Total Bookings", value: stats.bookings_count ?? 0, icon: CalendarClock, color: "text-orange-400" },
    { label: "Gross Revenue", value: money(stats.gross_revenue), icon: DollarSign, color: "text-emerald-400" },
    { label: "Provider Net (80%)", value: money(stats.provider_net), icon: Wallet, color: "text-fuchsia-400" },
    { label: "Platform Fees (20%)", value: money(stats.platform_fees), icon: Percent, color: "text-blue-400" },
  ];

  return (
    <div className="min-h-screen bg-[#060810]" data-testid="admin-car-operator">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button onClick={() => navigate("/admin?tab=car-providers")} data-testid="operator-back-btn" className="flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 mb-6"><ArrowLeft className="w-4 h-4" /> Back to Car Operators</button>

        {/* Header / business history */}
        <div className="glass neon-border rounded-2xl p-6 sm:p-8 mb-6" data-testid="operator-header">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shrink-0">
              {op.avatar_url ? <img src={fileUrl(op.avatar_url)} alt="" className="w-full h-full object-cover" /> : <KeyRound className="w-7 h-7 text-white" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-mono-caption uppercase tracking-[0.3em] text-cyan-400 mb-1">Car rental operator</p>
              <h1 className="font-heading font-extrabold text-white text-2xl" data-testid="operator-name">{profile.business_name || op.name}</h1>
              {op.admin_verified && <div className="mt-1"><AdminVerifiedBadge /></div>}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-400 mt-2">
                <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {op.email}</span>
                {op.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {op.phone}</span>}
                <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Joined {fmtDate(op.created_at)}</span>
              </div>
            </div>
            <span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${badge(profile.status)}`} data-testid="operator-status">{profile.status || "n/a"}</span>
          </div>
          {op.bio && <p className="text-slate-300 text-sm mt-4 leading-relaxed border-t border-[#1D2640] pt-4">{op.bio}</p>}
          {profile.reviewed_at && <p className="text-xs text-slate-500 mt-3">Application reviewed {fmtDate(profile.reviewed_at)}</p>}
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="operator-stats">
          {STAT_CARDS.map((s) => (
            <div key={s.label} className="glass rounded-xl p-4">
              <s.icon className={`w-5 h-5 ${s.color} mb-2`} />
              <p className="text-lg font-heading font-extrabold text-white">{s.value}</p>
              <p className="text-[11px] text-slate-400 uppercase tracking-wide">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Business documents */}
        <p className="font-heading font-bold text-white text-lg flex items-center gap-2 mb-3"><FileText className="w-4.5 h-4.5 text-orange-400" /> Business documents</p>
        <div className="flex flex-wrap gap-2 mb-8" data-testid="operator-documents">
          {DOCS.map(([label, url]) => (
            <a key={label} href={url ? fileUrl(url) : "#"} target="_blank" rel="noreferrer"
              className={`text-xs flex items-center gap-1.5 px-3 py-2 rounded-lg glass ${url ? "text-cyan-300 hover:text-white" : "text-slate-600 pointer-events-none"}`}>
              <FileText className="w-3.5 h-3.5" /> {label} {url && <ExternalLink className="w-3 h-3" />}
            </a>
          ))}
        </div>

        {/* Vehicles */}
        <p className="font-heading font-bold text-white text-lg flex items-center gap-2 mb-3"><Car className="w-4.5 h-4.5 text-cyan-400" /> Registered vehicles ({op.vehicles.length})</p>
        {op.vehicles.length === 0 ? (
          <div className="glass rounded-xl p-6 text-center text-slate-500 mb-8">No vehicles registered yet.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="operator-vehicles">
            {op.vehicles.map((v) => (
              <div key={v.id} className="glass rounded-xl overflow-hidden" data-testid={`operator-vehicle-${v.id}`}>
                <div className="h-32 bg-[#0B0E1B] overflow-hidden">
                  {v.image_url ? <img src={fileUrl(v.image_url)} alt={v.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Car className="w-8 h-8 text-slate-700" /></div>}
                </div>
                <div className="p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-white truncate">{v.name}</p>
                    <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border shrink-0 ${badge(v.status)}`}>{v.status}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{v.car_type} · {v.island}</p>
                  <p className="text-sm text-cyan-300 font-semibold mt-1">{money(v.price_per_day, v.currency)}/day</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bookings table */}
        <p className="font-heading font-bold text-white text-lg flex items-center gap-2 mb-3"><CalendarClock className="w-4.5 h-4.5 text-orange-400" /> Rental bookings ({op.bookings.length})</p>
        {op.bookings.length === 0 ? (
          <div className="glass rounded-xl p-6 text-center text-slate-500">No rental bookings processed yet.</div>
        ) : (
          <div className="glass rounded-xl overflow-x-auto" data-testid="operator-bookings">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-[#1D2640]">
                  <th className="px-4 py-3 font-semibold">Vehicle</th>
                  <th className="px-4 py-3 font-semibold">Traveler</th>
                  <th className="px-4 py-3 font-semibold">Dates</th>
                  <th className="px-4 py-3 font-semibold text-right">Total</th>
                  <th className="px-4 py-3 font-semibold text-right">Net (80%)</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Booked</th>
                </tr>
              </thead>
              <tbody>
                {op.bookings.map((b) => (
                  <tr key={b.id} className="border-b border-[#141B2E] last:border-0 hover:bg-white/[0.02]" data-testid={`operator-booking-${b.id}`}>
                    <td className="px-4 py-3 text-white font-medium">{b.car_name}</td>
                    <td className="px-4 py-3 text-slate-300">{b.traveler_name}</td>
                    <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{fmtDate(b.pickup_date)} → {fmtDate(b.dropoff_date)}</td>
                    <td className="px-4 py-3 text-right text-white">{money(b.total, b.currency)}</td>
                    <td className="px-4 py-3 text-right text-emerald-400">{money(b.provider_net, b.currency)}</td>
                    <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${badge(b.status)}`}>{b.status}</span></td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(b.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
